import * as React from 'react';
import getISOWeek from 'date-fns/getISOWeek';
import { get as getCookie } from 'js-cookie';

import { UserData, DeviceType, Browser, USER_COOKIE, ProductGridSlotConfig } from '@packages/common';
import { getPublicRuntimeConfig } from '@helpers/getPublicRuntimeConfig';

interface AppStaticStateProviderProps {
  cookies: { [key: string]: string };
  deviceType: DeviceType;
  browser: Browser;
  productGridSlotConfig: ProductGridSlotConfig;
  url: string;
  originalLocation: string;
}

export interface AppStaticStateContextValue extends Omit<AppStaticStateProviderProps, 'cookies'> {
  userData: UserData;
  clientID: string | undefined;
  sessionID: string | undefined;
  calendarWeek: number | undefined;
  locale: string;
  domain: string;
  environment: string;
  originalLocation: string;
}

export const AppStaticStateContext = React.createContext<AppStaticStateContextValue>({} as any);

const defaultHash = {
  hash: '',
  created: '',
  modified: '',
};

export function getUserData(cookies: AppStaticStateProviderProps['cookies']): UserData {
  if (cookies && cookies[USER_COOKIE.name]) {
    try {
      return JSON.parse(cookies[USER_COOKIE.name]) as UserData;
    } catch (error) {
      console.error(error);
      return defaultHash;
    }
  }
  return defaultHash;
}

const LOCALE = getPublicRuntimeConfig('LOCALE');
const TARGET_ENV = getPublicRuntimeConfig('TARGET_ENV');
const HOST_NAME = getPublicRuntimeConfig('HOST_NAME');

export const AppStaticStateProvider: React.FC<AppStaticStateProviderProps> = ({
  children,
  cookies,
  deviceType,
  browser,
  productGridSlotConfig,
  url,
  originalLocation,
}) => {
  const cookiesRef = React.useRef(cookies);

  const value = React.useRef({
    deviceType,
    browser,
    userData: getUserData(cookiesRef.current),
    clientID: getCookie('_ga'),
    sessionID: getCookie('scom'),
    calendarWeek: getISOWeek(Date.now()),
    environment: TARGET_ENV,
    locale: LOCALE,
    domain: `https://${HOST_NAME}`,
    productGridSlotConfig,
    url,
    originalLocation: `https://${HOST_NAME}${originalLocation}`,
  });
  return <AppStaticStateContext.Provider value={value.current}>{children}</AppStaticStateContext.Provider>;
};
